<template>
  <b-modal
    :visible="true"
    title="Simular fluxo"
    size="lg"
    no-close-on-backdrop
    hide-footer
    @hide="$emit('fechar')"
  >
    <small class="text-muted d-block mb-1">
      Simula o rascunho atual do fluxo (não precisa salvar antes) - nenhuma mensagem real é enviada pelo WhatsApp.
    </small>

    <div ref="transcript" class="simulador-transcript">
      <div
        v-for="(item, indice) in transcript"
        :key="indice"
        class="simulador-linha"
        :class="'simulador-' + item.tipo"
      >
        <div class="simulador-bolha">
          <small v-if="item.tipo === 'sistema'" class="d-block" style="white-space: pre-wrap;">
            <feather-icon icon="InfoIcon" size="14" class="mr-25" />{{ item.texto }}
          </small>
          <template v-else>
            <img v-if="item.imagem" :src="item.imagem" class="simulador-imagem" />
            <span v-if="item.texto" class="d-block" style="white-space: pre-wrap;">{{ item.texto }}</span>
          </template>
        </div>
      </div>
    </div>

    <div v-if="aguardando" class="mt-1">
      <template v-if="aguardando.tipo === 'botoes' || aguardando.tipo === 'lista'">
        <b-button
          v-for="opcao in aguardando.opcoes"
          :key="opcao.chave"
          variant="outline-primary"
          size="sm"
          class="mr-50 mb-50"
          @click="responderOpcao(opcao)"
        >
          {{ opcao.rotulo }}
        </b-button>
        <small class="d-block text-muted mt-25">
          Ou digite algo diferente pra testar a resposta de fallback:
        </small>
      </template>

      <div class="d-flex mt-50">
        <b-form-input
          v-model="textoDigitado"
          placeholder="Digite uma resposta..."
          @keydown.enter="responderTexto"
        />
        <b-button variant="primary" class="ml-50" @click="responderTexto">
          Enviar
        </b-button>
      </div>
    </div>

    <div v-else-if="finalizado" class="mt-1 text-center">
      <b-badge variant="light-secondary">Simulação encerrada</b-badge>
    </div>

    <div class="mt-1 d-flex justify-content-between align-items-center">
      <small v-if="Object.keys(variaveis).length" class="text-muted">
        Variáveis: {{ JSON.stringify(variaveis) }}
      </small>
      <span v-else />
      <b-button size="sm" variant="outline-secondary" @click="iniciar">
        <feather-icon icon="RefreshCwIcon" size="14" /> Reiniciar
      </b-button>
    </div>
  </b-modal>
</template>

<script>
import { BButton, BFormInput, BBadge } from 'bootstrap-vue'
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue'
import { saidasDoNo, textoChamado, ROTULOS_ABRIR_CHAMADO } from '@/utils/fluxoSchema'

const CONTATO_FALSO = { nome: 'Cliente Teste', nome_perfil: 'Cliente Teste', wa_id: '5511999999999' }
const MAX_ITERACOES = 50

export default {
  components: { BButton, BFormInput, BBadge, FeatherIcon },
  props: {
    definicao: { type: Object, required: true },
    departamentos: { type: Array, default: () => [] },
  },
  data() {
    return {
      transcript: [],
      noAtualId: null,
      variaveis: {},
      tentativasUnidade: {},
      aguardando: null,
      finalizado: false,
      textoDigitado: '',
    }
  },
  mounted() {
    this.iniciar()
  },
  methods: {
    iniciar() {
      this.transcript = []
      this.variaveis = {}
      this.tentativasUnidade = {}
      this.aguardando = null
      this.finalizado = false
      this.textoDigitado = ''
      this.noAtualId = this.definicao.no_inicial

      if (!this.noAtualId) {
        this.transcript.push({ tipo: 'sistema', texto: 'Este fluxo não tem um nó inicial definido.' })
        this.finalizado = true
        return
      }

      this.executar()
    },
    interpolar(texto) {
      return String(texto || '').replace(/\{\{\s*([a-zA-Z0-9_.]+)\s*\}\}/g, (match, chave) => {
        if (chave.indexOf('contato.') === 0) {
          return CONTATO_FALSO[chave.substring('contato.'.length)] || ''
        }
        return this.variaveis[chave] !== undefined ? this.variaveis[chave] : ''
      })
    },
    encontrarNo(id) {
      return this.definicao.nos.find(n => n.id === id) || null
    },
    async executar() {
      for (let iteracao = 0; iteracao < MAX_ITERACOES; iteracao++) {
        const no = this.encontrarNo(this.noAtualId)

        if (!no) {
          this.transcript.push({ tipo: 'sistema', texto: 'Nó "' + this.noAtualId + '" não existe mais - simulação interrompida.' })
          this.finalizado = true
          return
        }

        const resultado = await this.executarNo(no)
        this.rolarParaBaixo()

        if (resultado.parar) return
        this.noAtualId = resultado.proximo
      }

      this.transcript.push({ tipo: 'sistema', texto: 'Limite de passos atingido - possível ciclo sem nó de espera.' })
      this.finalizado = true
    },
    async executarNo(no) {
      const conteudo = no.conteudo || {}

      switch (no.tipo) {
        case 'inicio':
          return this.avancarUnico(no)

        case 'mensagem':
          this.transcript.push({ tipo: 'bot', texto: this.interpolar(conteudo.texto) })
          return this.avancarUnico(no)

        case 'imagem': {
          let imagem = null
          if (conteudo.midia_id) {
            try {
              const resposta = await window.axios.get(this.$store.state.api + 'fluxo-midias/' + conteudo.midia_id)
              imagem = resposta.data.url_assinada
            } catch (e) { imagem = null }
          }
          this.transcript.push({ tipo: 'bot', imagem, texto: imagem ? this.interpolar(conteudo.legenda) : '(imagem não encontrada)' })
          return this.avancarUnico(no)
        }

        case 'captura':
          if (conteudo.texto) this.transcript.push({ tipo: 'bot', texto: this.interpolar(conteudo.texto) })
          this.aguardando = { tipo: 'captura', no }
          return { parar: true }

        case 'unidade':
          this.transcript.push({ tipo: 'bot', texto: this.interpolar(conteudo.texto) })
          this.aguardando = { tipo: 'unidade', no }
          return { parar: true }

        case 'botoes':
          this.transcript.push({ tipo: 'bot', texto: this.interpolar(conteudo.texto) })
          this.aguardando = { tipo: 'botoes', no, opcoes: saidasDoNo(no) }
          return { parar: true }

        case 'lista': {
          this.transcript.push({ tipo: 'bot', texto: this.interpolar(conteudo.texto) })
          this.aguardando = { tipo: 'lista', no, opcoes: saidasDoNo(no) }
          return { parar: true }
        }

        // Nao abre chamado de verdade: mostra o que iria pro GLPI e segue
        // por "Chamado criado" com um numero ficticio.
        case 'chamado_glpi': {
          const tipo = Number(conteudo.tipo) === 2 ? 'Requisição' : 'Incidente'
          const linhas = [
            'Chamado GLPI (simulado, nada é criado)',
            'Tipo: ' + tipo + (conteudo.categoria_nome ? ' - ' + conteudo.categoria_nome : ''),
            'Título: ' + this.interpolar(conteudo.titulo),
          ]
          if (conteudo.descricao) linhas.push('Descrição: ' + this.interpolar(conteudo.descricao))
          const abrirComo = conteudo.abrir_como || 'aberto'
          linhas.push('Status: ' + ROTULOS_ABRIR_CHAMADO[abrirComo] + (abrirComo !== 'aberto' ? ' - solução: ' + this.interpolar(conteudo.solucao) : ''))
          const camposGlpi = conteudo.campos_glpi || []
          camposGlpi.forEach(campo => {
            linhas.push(campo.rotulo + ': ' + (campo.tipo === 'lista' ? campo.valor_rotulo : this.interpolar(campo.valor)))
          })
          if (conteudo.incluir_conversa) linhas.push('+ conversa do atendimento')
          if (!this.variaveis.unidade_codigo) linhas.push('Atenção: sem unidade identificada, o chamado iria sem localização')
          const aguarde = textoChamado(conteudo, 'mensagem_aguarde')
          if (String(aguarde).trim()) this.transcript.push({ tipo: 'bot', texto: this.interpolar(aguarde) })
          this.transcript.push({ tipo: 'sistema', texto: linhas.join('\n') })
          this.variaveis = { ...this.variaveis, glpi_chamado_id: '0000' }
          const sucesso = textoChamado(conteudo, 'mensagem_sucesso')
          if (String(sucesso).trim()) this.transcript.push({ tipo: 'bot', texto: this.interpolar(sucesso) })
          return this.avancarPelaChave(no, 'criado')
        }

        case 'condicao': {
          const valorAtual = this.variaveis[conteudo.variavel]
          let resultado
          if (conteudo.operador === 'diferente') resultado = valorAtual != conteudo.valor
          else if (conteudo.operador === 'contem') resultado = typeof valorAtual === 'string' && valorAtual.toLowerCase().indexOf(String(conteudo.valor || '').toLowerCase()) !== -1
          else resultado = valorAtual == conteudo.valor
          this.transcript.push({ tipo: 'sistema', texto: 'Condição "' + conteudo.variavel + '" ' + (resultado ? 'verdadeira' : 'falsa') })
          return this.avancarPelaChave(no, resultado ? 'sim' : 'nao')
        }

        case 'horario': {
          let dentro = false
          if (conteudo.departamento_id) {
            try {
              const resposta = await window.axios.get(this.$store.state.api + 'departamentos/' + conteudo.departamento_id + '/dentro-do-horario')
              dentro = resposta.data.dentro
            } catch (e) { dentro = false }
          }
          this.transcript.push({ tipo: 'sistema', texto: 'Horário: ' + (dentro ? 'dentro' : 'fora') + ' do expediente (checagem real)' })
          return this.avancarPelaChave(no, dentro ? 'dentro' : 'fora')
        }

        case 'transferir': {
          const dep = this.departamentos.find(d => d.id === conteudo.departamento_id)
          this.transcript.push({ tipo: 'sistema', texto: '→ Transferido para a fila de "' + (dep ? dep.nome : '?') + '"' })
          this.finalizado = true
          return { parar: true }
        }

        case 'aguardar':
          this.transcript.push({ tipo: 'sistema', texto: '(aguardaria ' + (conteudo.segundos || 0) + 's - simulação avança direto)' })
          return this.avancarUnico(no)

        case 'fim':
          this.transcript.push({ tipo: 'sistema', texto: 'Atendimento encerrado pela URA' })
          this.finalizado = true
          return { parar: true }

        default:
          this.transcript.push({ tipo: 'sistema', texto: 'Tipo de nó desconhecido: ' + no.tipo })
          this.finalizado = true
          return { parar: true }
      }
    },
    avancarUnico(no) {
      const destino = (no.saidas || [])[0] ? no.saidas[0].destino : null
      return { parar: !destino, proximo: destino }
    },
    avancarPelaChave(no, chave) {
      const saida = (no.saidas || []).find(s => s.chave === chave)
      return saida && saida.destino ? { parar: false, proximo: saida.destino } : { parar: true }
    },
    responderOpcao(opcao) {
      const no = this.aguardando.no
      this.transcript.push({ tipo: 'usuario', texto: opcao.rotulo })
      this.aguardando = null
      const resultado = this.avancarPelaChave(no, opcao.chave)
      if (resultado.parar) {
        this.finalizado = true
      } else {
        this.noAtualId = resultado.proximo
        this.executar()
      }
    },
    // Mesma regra do MotorFluxo::executarUnidade, consultando o cadastro
    // real de unidades - so nao grava o vinculo.
    async responderUnidade(no, valor) {
      const conteudo = no.conteudo || {}
      let unidade = null
      try {
        const resposta = await window.axios.get(this.$store.state.api + 'unidades/codigo/' + encodeURIComponent(valor))
        unidade = resposta.data
      } catch (e) { unidade = null }

      let resultado
      if (unidade) {
        this.tentativasUnidade = { ...this.tentativasUnidade, [no.id]: 0 }
        this.variaveis = { ...this.variaveis, unidade_codigo: String(unidade.codigo_cliente), unidade_nome: unidade.nome }
        this.transcript.push({ tipo: 'sistema', texto: 'Atendimento vinculado à unidade ' + unidade.codigo_cliente + ' - ' + unidade.nome })
        resultado = this.avancarPelaChave(no, 'encontrada')
      } else {
        const tentativas = (this.tentativasUnidade[no.id] || 0) + 1
        const maxTentativas = Math.max(parseInt(conteudo.max_tentativas, 10) || 3, 1)
        if (tentativas >= maxTentativas) {
          this.tentativasUnidade = { ...this.tentativasUnidade, [no.id]: 0 }
          this.transcript.push({ tipo: 'sistema', texto: 'Unidade não encontrada após ' + tentativas + ' tentativa(s)' })
          resultado = this.avancarPelaChave(no, 'nao_encontrada')
        } else {
          this.tentativasUnidade = { ...this.tentativasUnidade, [no.id]: tentativas }
          this.transcript.push({ tipo: 'bot', texto: this.interpolar(conteudo.texto_invalido || 'Código do cliente não encontrado. Confira e envie novamente.') })
          this.aguardando = { tipo: 'unidade', no }
          this.rolarParaBaixo()
          return
        }
      }

      if (resultado.parar) { this.finalizado = true; return }
      this.noAtualId = resultado.proximo
      this.executar()
    },
    responderTexto() {
      const valor = this.textoDigitado.trim()
      if (!valor || !this.aguardando) return
      this.transcript.push({ tipo: 'usuario', texto: valor })
      this.textoDigitado = ''

      const no = this.aguardando.no
      const tipoAguardando = this.aguardando.tipo

      if (tipoAguardando === 'unidade') {
        this.aguardando = null
        this.responderUnidade(no, valor)
        return
      }

      if (tipoAguardando === 'captura') {
        if (no.conteudo && no.conteudo.nome_variavel) {
          this.variaveis = { ...this.variaveis, [no.conteudo.nome_variavel]: valor }
        }
        this.aguardando = null
        const resultado = this.avancarUnico(no)
        if (resultado.parar) { this.finalizado = true; return }
        this.noAtualId = resultado.proximo
        this.executar()
        return
      }

      // botoes/lista: texto livre nao corresponde a nenhuma chave -> fallback,
      // igual o MotorFluxo faz de verdade (reenvia a mesma pergunta)
      this.aguardando = null
      this.transcript.push({ tipo: 'sistema', texto: 'Resposta não reconhecida - repetindo a pergunta (' + (this.definicao.mensagem_fallback || 'fallback') + ')' })
      this.executar()
    },
    rolarParaBaixo() {
      this.$nextTick(() => {
        const el = this.$refs.transcript
        if (el) el.scrollTop = el.scrollHeight
      })
    },
  },
}
</script>

<style scoped>
.simulador-transcript {
  height: 360px;
  overflow-y: auto;
  background: #f2f0f7;
  border-radius: 6px;
  padding: 1rem;
}
.simulador-linha {
  display: flex;
  margin-bottom: 0.5rem;
}
.simulador-bot {
  justify-content: flex-start;
}
.simulador-usuario {
  justify-content: flex-end;
}
.simulador-sistema {
  justify-content: center;
}
.simulador-bolha {
  max-width: 75%;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  background: #fff;
}
.simulador-imagem {
  max-width: 220px;
  max-height: 220px;
  border-radius: 4px;
}
.simulador-usuario .simulador-bolha {
  background: #7367f0;
  color: #fff;
}
.simulador-sistema .simulador-bolha {
  background: transparent;
  color: #6e6b7b;
  font-style: italic;
  max-width: 100%;
  text-align: center;
}
</style>
